import { of, Observable } from 'rxjs';
import { map, flatMap, zip } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { DataService, ApiService, InspectionPluginService, LanguageService } from '../../shared/services/index';
import { InspectionPlugin } from '../../shared/models/index';
import { FilesPluginFactoryService } from './files-plugin-factory.service';

@Injectable()
export class FilesPluginService extends InspectionPluginService {
  private editedImagesIds = [];
  private comments: any = {};
  private newAssets = [];
  private plugin: InspectionPlugin;

  public toTypeKeys: any = [
    { value: 1, label: 'plugins.add-photos.technical-service' },
    { value: 2, label: 'plugins.add-photos.customer' }
  ];
  public toTypes: any = [];

  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    private language: LanguageService,
    private filesFactory: FilesPluginFactoryService
  ) {
    super(apiService, dataService, null, filesFactory);
    this.applyTranslations();
    this.language.onLanguageChange().subscribe((value) => this.applyTranslations());
  }

  public applyTranslations() {
    this.toTypes = [];
    this.copyTranslationValues(this.toTypeKeys, this.toTypes, this.language);
  }

  public getToTypes() {
    return this.toTypes;
  }

  public addFiles(inspectionId: string, pointId: string, pluginId: string, files: any): Observable<any> {
    return this._addFiles(inspectionId, pointId, pluginId, files).pipe(
      map((data: any) => {
        this.plugin = data.plugin;
        this.newAssets.unshift(...data.newAssets);
        this.dataService.updatePlugin(inspectionId, this.plugin);
        return data.newAssets;
      })
    );
  }

  public getNewAssets() {
    return this.newAssets;
  }

  public save(inspectionId: string) {
    this.plugin = null;
    this.newAssets = [];
  }

  public deleteFile(inspectionId: string, pointId: string, pluginId: string, assetId: string): Observable<any> {
    return this._deleteFile(inspectionId, pointId, pluginId, assetId).pipe(
      map((res: InspectionPlugin) => {
        this.newAssets = this.newAssets.filter((a) => a !== assetId);
        return this.dataService.deleteFile(inspectionId, res);
      })
    );
  }

  public editFile(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string,
    points: any
  ): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/assets/${assetId}/file`;
    return this.apiService.post(url, points).pipe(
      map((res) => {
        this.addEditedImage(assetId);
        return res;
      })
    );
  }

  public editComment(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string,
    data: any,
    photo
  ): Observable<any> {
    return this._editComment(inspectionId, pointId, pluginId, assetId, data, photo).pipe(
      map((res: InspectionPlugin) => {
        if (photo > 0) {
          this.addEditedImage(assetId);
        }
        return this.dataService.updatePlugin(inspectionId, res);
      })
    );
  }

  public downloadImage(inspectionId: string, assetId: string) {
    const url = `api/inspections/${inspectionId}/assets/${assetId}/download`;
    if (this.isInspectionInOffline(inspectionId)) {
      this.filesFactory
        .getImageUrl(inspectionId, assetId)
        .subscribe((res) => window.open(res.changingThisBreaksApplicationSecurity, '_blank'));
    } else {
      window.open(url, '_blank');
    }
  }

  public cancel(inspectionId: string, pointId: string, pluginId: string) {
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/assets/delete`;
    if (this.newAssets.length > 0) {
      return this._cancel(inspectionId, pointId, pluginId).pipe(
        map((res) => {
          this.plugin = null;
          this.newAssets = [];
          return this.dataService.updatePlugin(inspectionId, res);
        })
      );
    }
    return of(null);
  }

  public getEditedImagesIds() {
    return of(this.editedImagesIds);
  }

  private addEditedImage(id) {
    if (!this.editedImagesIds.some((i) => i === id)) {
      this.editedImagesIds.push(id);
    }
  }

  public addLocalComment(assetId: string, comment: string) {
    this.comments[assetId] = comment;
  }

  public getLocalComment(assetId: string) {
    return this.comments[assetId] ? this.comments[assetId] : '';
  }

  public deleteLocalComment(assetId: string) {
    this.comments[assetId] = '';
  }

  public deleteFilesPlugin(inspectionId: string, pointId: string, pluginId: string, assets: string[]): any {
    return this.filesFactory.deleteFiles(inspectionId, pointId, pluginId, assets).pipe(
      flatMap((res) => {
        return super.deletePlugin(inspectionId, pointId, pluginId);
      }),
      map((success) => success)
    );
  }

  private _addFiles(inspectionId: string, pointId: string, pluginId: string, files: any): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/files/${pluginId}`;
    return this.filesFactory.addFiles(inspectionId, pointId, pluginId, files).pipe(
      flatMap((data) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(data);
        }
        data.assetValues.forEach((assetValue) => files.append(assetValue.key, assetValue.assetId));
        // bug here, if postFormData fails, the postconfig can succeeds ... maybe do them in sequence ?
        return this.apiService.postFormData(url, files).pipe(
          zip(this.apiService.post(data.plugin.postServerUrl, data.plugin.config)),
          map(([newAssets, plugin]) => ({ newAssets, plugin }))
        );
      })
    );
  }
  private _deleteFile(inspectionId: string, pointId: string, pluginId: string, assetId: string): Observable<any> {
    return this.filesFactory.deleteFile(inspectionId, pointId, pluginId, assetId).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(plugin.postServerUrl, plugin.config);
      })
    );
  }

  private _editComment(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string,
    data: any,
    photo
  ): Observable<any> {
    const inputFile = new FormData();
    if (photo) {
      inputFile.append('images', photo);
    }
    inputFile.append('imageAssets[0]', assetId);

    const postFormUrl = `/inspections/${inspectionId}/points/${pointId}/files/${pluginId}`;
    const url = `/inspections/${inspectionId}/points/${pointId}/plugins/${pluginId}/assets/${assetId}/edit/comment`;
    return (photo ? this.filesFactory.editFile(inspectionId, pointId, pluginId, assetId, photo) : of(null)).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return photo ? this.apiService.postFormData(postFormUrl, inputFile) : of(null);
      }),
      flatMap((plugin) => {
        return this.filesFactory.editComment(inspectionId, pointId, pluginId, assetId, data);
      }),
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(url, data);
      })
    );
  }

  private _cancel(inspectionId: string, pointId: string, pluginId: string) {
    if (this.newAssets.length > 0) {
      return this.filesFactory.deleteFiles(inspectionId, pointId, pluginId, this.newAssets).pipe(
        flatMap((plugin) => {
          if (this.isInspectionInOffline(inspectionId)) {
            return of(plugin);
          }
          return this.apiService.post(plugin.postServerUrl, plugin.config);
        })
      );
    }
    return of(null);
  }
}
